import React from 'react';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import List from '@/components/organisms/SearchResults/List';
import useTLCSpecialities from '@/src/hooks/useTLCSpecialities';
import { APP_ROUTES, ENTIRE_FRANCE_DISTANCE_MAX } from '@/src/constants';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import useUpcomingTLCs from '@/src/hooks/tlc/useUpcomingTLCs';
import ListHeader from '@/components/organisms/SearchResults/ListHeader';
import SeeMoreButton from '@/components/organisms/SearchResults/SeeMoreButton';
import { LIMITATION_ALL_PATIENTS } from '@/components/molecules/FilterBar/TLCPatientLimitationSwitch';

type Props = {
  filters: Record<string, any>;
  onItemsChange?: (items?: CardHcd[]) => void;
};

const addressQueryFromFilters = (filters: Record<string, any>) => {
  if (filters.address?.zipCode) {
    return {
      locality: `${filters.address.zipCode}-${filters.address.shortName}`,
    };
  }
  if (filters.address?.longtitude && filters.address?.latitude) {
    return {
      locality: filters.address?.label,
      longtitude: filters.address?.longtitude,
      latitude: filters.address?.latitude,
    };
  }
  return {
    locality: '75000-PARIS',
  };
};

const UpcomingTlCs = ({ filters, onItemsChange }: Props) => {
  const { currentSpeciality } = useTLCSpecialities();
  const specialityShortName = currentSpeciality.value;

  const upcomingTLCs = useUpcomingTLCs({
    ...filters,
    speciality: specialityShortName,
  });

  React.useEffect(() => {
    onItemsChange?.(upcomingTLCs);
  }, [upcomingTLCs, onItemsChange]);

  const onMorePractitionersClick = React.useCallback(() => {
    analyticsEvent({
      category: 'DirectTLC',
      action: 'ClickMoreAppointmentTLCinDirectTLC',
      label: specialityShortName,
    });
  }, [specialityShortName]);

  const onListItemClick = React.useCallback(() => {
    analyticsEvent({
      category: 'DirectTLC',
      action: 'ClickAppointmentTLCinDirectTLC',
      label: specialityShortName,
    });
  }, [specialityShortName]);

  if (upcomingTLCs.length === 0) {
    return null;
  }

  const { extraFeeFilter, newPatientFilter } = filters;
  const seeMoreButton = (
    <SeeMoreButton
      href={{
        pathname: APP_ROUTES.SPECIALITY.LOCALITY,
        query: {
          speciality: currentSpeciality.value,
          distanceMax: ENTIRE_FRANCE_DISTANCE_MAX,
          consultationModeFilter: 'TLC',
          newPatientFilter: newPatientFilter || LIMITATION_ALL_PATIENTS,
          'availabilityFilter.type': 'TODAY',
          ...(extraFeeFilter && { extraFeeFilter }),
          ...addressQueryFromFilters(filters),
        },
      }}
      onClick={onMorePractitionersClick}
    />
  );

  return (
    <div className="w-full">
      <div className="flex flex-col gap-3" data-testid="upcoming-tlc-list">
        <List
          items={upcomingTLCs}
          mode="upcomingTlc"
          onItemClick={onListItemClick}
          header={
            <ListHeader
              titleKey="upcoming-tlcs-title"
              subtitleKey="upcoming-tlcs-subtitle"
              rightSideElement={seeMoreButton}
            />
          }
        />
      </div>
    </div>
  );
};

export default UpcomingTlCs;
